import { Box } from '@mui/material';
import { TalentItem, TalentItemSkeleton, TalentPreviewSkeleton } from './components';
import { TALENT_ITEM_DATA, TALENT_PROFILE_DATA } from './data/mockData';
import { useState, useRef, useCallback, useEffect } from 'react';
import TalentPreview from './components/TalentPreview';

const ITEMS_PER_PAGE = 10;
const TOTAL_ITEMS = 100; // Total mock items available

const TalentListPreview = () => {
    const [selectedTalent, setSelectedTalent] = useState<typeof TALENT_PROFILE_DATA>();
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [displayedItems, setDisplayedItems] = useState(0);
    const [isGettingList, setIsGettingList] = useState(false);
    const [isGettingDetail, setIsGettingDetail] = useState(false);
    const observerTarget = useRef<HTMLDivElement>(null);

    const loadMore = useCallback(() => {
        if (displayedItems >= TOTAL_ITEMS || isGettingList) return;

        setIsGettingList(true);
        // Simulate async loading
        setTimeout(() => {
            setDisplayedItems(prev => Math.min(prev + ITEMS_PER_PAGE, TOTAL_ITEMS));
            if (displayedItems === 0) {
                setSelectedTalent({ ...TALENT_PROFILE_DATA, id: '1' });
                setSelectedId('1');
            }
            setIsGettingList(false);
        }, 1500);
    }, [displayedItems, isGettingList]);

    const handleSelectTalent = (id: string) => {
        setSelectedId(id);
        setIsGettingDetail(true);
        setTimeout(() => {
            setSelectedTalent({ ...TALENT_PROFILE_DATA, id });
            setIsGettingDetail(false);
        }, 800);
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting) {
                    loadMore();
                }
            },
            { threshold: 0.1 },
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [loadMore]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexBasis: '0px',
                flexGrow: 1,
                flexShrink: 1,
                overflow: 'hidden',
            }}>
            <Box
                sx={{
                    width: 313,
                    overflowY: 'auto',
                    paddingRight: '10px',
                }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {Array.from({ length: displayedItems }).map((_, index) => (
                        <TalentItem
                            key={index}
                            isActive={selectedId === String(index + 1)}
                            {...{ ...TALENT_ITEM_DATA, id: String(index + 1) }}
                            onClick={handleSelectTalent}
                        />
                    ))}
                    {isGettingList &&
                        Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                            <TalentItemSkeleton key={`skeleton-${index}`} />
                        ))}
                    {displayedItems < TOTAL_ITEMS && <Box ref={observerTarget} sx={{ height: 1 }} />}
                </Box>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '0 10px',
                }}>
                {isGettingDetail || !selectedTalent ? (
                    <TalentPreviewSkeleton />
                ) : (
                    <TalentPreview />
                )}
            </Box>
        </Box>
    );
};

export default TalentListPreview;
